import React, { useMemo } from 'react';
import { alpha } from '@mui/material/styles';
import { Box, Grid, LinearProgress, Paper, Typography } from '@mui/material';
import HotelOutlinedIcon from '@mui/icons-material/HotelOutlined';
import RestaurantOutlinedIcon from '@mui/icons-material/RestaurantOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import SouthWestOutlinedIcon from '@mui/icons-material/SouthWestOutlined';
import NorthEastOutlinedIcon from '@mui/icons-material/NorthEastOutlined';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import SyncAltOutlinedIcon from '@mui/icons-material/SyncAltOutlined';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  getStockChartCardSx,
  getStockChartMargin,
  getStockChartPlotSx,
  getStockDashboardSectionSx,
  getStockLegendProps,
  isStockPdfExport,
} from '../../utils/stockMobileLayout';
import { THEME_COLORS } from '../../shared/constants/theme';
import { ChartAreaSkeleton, MetricValueSkeleton, StatValueSkeleton } from './StockDashboardPartials';
import { getInsightsGridSpacing, getInsightsMetricGridProps } from '../../utils/insightsDashboardLayout';

const formatQty = (value) => {
  const n = Number(value || 0);
  return n.toLocaleString('en-IN', { maximumFractionDigits: 2 });
};

const formatCurrency = (value) => {
  const n = Number(value || 0);
  return `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
};

const formatCurrencyDetailed = (value) => {
  const n = Number(value || 0);
  return `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
};

const getMetricTileBackground = (isDark, color) => {
  if (isDark && isStockPdfExport()) {
    return `linear-gradient(160deg, ${alpha(color, 0.16)} 0%, ${THEME_COLORS.SURFACE_ELEVATED_DARK} 100%)`;
  }
  return isDark
    ? `linear-gradient(160deg, ${alpha(color, 0.12)} 0%, rgba(255,255,255,0.02) 100%)`
    : `linear-gradient(160deg, ${alpha(color, 0.1)} 0%, rgba(255,255,255,0.98) 100%)`;
};

const getUtilizationBackground = (isDark, accentColor) => {
  if (isDark && isStockPdfExport()) {
    return `radial-gradient(120% 90% at 10% 0%, ${alpha(accentColor, 0.18)} 0%, ${THEME_COLORS.SURFACE_ELEVATED_DARK} 55%)`;
  }
  return isDark
    ? `radial-gradient(120% 90% at 10% 0%, ${alpha(accentColor, 0.14)} 0%, rgba(255,255,255,0.02) 55%)`
    : `radial-gradient(120% 90% at 10% 0%, ${alpha(accentColor, 0.1)} 0%, rgba(255,255,255,0.98) 55%)`;
};

const MetricTile = ({ label, value, subValue, color, icon, progress, isDark, themeColors, loading = false }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2,
      height: '100%',
      borderRadius: 2.5,
      border: `1px solid ${alpha(color, isDark ? 0.28 : 0.22)}`,
      background: getMetricTileBackground(isDark, color),
      boxShadow: isDark ? 'inset 0 1px 0 rgba(255,255,255,0.04)' : '0 4px 14px rgba(15,23,42,0.04)',
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color,
          bgcolor: alpha(color, isDark ? 0.16 : 0.12),
          border: `1px solid ${alpha(color, 0.24)}`,
        }}
      >
        {icon}
      </Box>
      <Typography sx={{ color: themeColors.subtleText, fontSize: '0.72rem', fontWeight: 700, letterSpacing: 0.6 }}>
        {label}
      </Typography>
    </Box>
    {loading ? (
      <MetricValueSkeleton isDark={isDark} />
    ) : (
      <>
        <Typography sx={{ color: themeColors.text, fontWeight: 800, fontSize: '1.45rem', lineHeight: 1.15 }}>
          {value}
        </Typography>
        {subValue ? (
          <Typography sx={{ color: themeColors.subtleText, fontSize: '0.76rem', mt: 0.35 }}>
            {subValue}
          </Typography>
        ) : null}
        <LinearProgress
          variant="determinate"
          value={Math.min(100, Math.max(0, progress))}
          sx={{
            mt: 1.5,
            height: 6,
            borderRadius: 999,
            bgcolor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.08)',
            '& .MuiLinearProgress-bar': {
              borderRadius: 999,
              background: `linear-gradient(90deg, ${color} 0%, ${alpha(color, 0.55)} 100%)`,
            },
          }}
        />
      </>
    )}
  </Paper>
);

const computePoolUtilization = (pool) => {
  const current = Number(pool?.current_inventory_qty || 0);
  const movement = Number(pool?.stock_in || 0) + Number(pool?.stock_out || 0);
  if (!current) return 0;
  return Math.min(100, Math.round((movement / current) * 100));
};

const UtilizationFlowVisual = ({
  title,
  utilization,
  stockIn,
  stockOut,
  accentColor,
  themeColors,
  isDark,
  loading = false,
}) => {
  if (loading) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 2,
          height: '100%',
          borderRadius: 2.5,
          border: `1px solid ${alpha(accentColor, isDark ? 0.22 : 0.16)}`,
          background: getUtilizationBackground(isDark, accentColor),
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <SyncAltOutlinedIcon sx={{ fontSize: 18, color: themeColors.success }} />
          <Typography sx={{ color: themeColors.subtleText, fontSize: '0.72rem', fontWeight: 700, letterSpacing: 0.6 }}>
            {title}
          </Typography>
        </Box>
        <ChartAreaSkeleton isDark={isDark} height={112} />
      </Paper>
    );
  }

  const movement = stockIn + stockOut;
  const inRatio = movement ? stockIn / movement : 0.5;
  const radius = 46;
  const circumference = 2 * Math.PI * radius;
  const utilStroke = (utilization / 100) * circumference;
  const inStroke = inRatio * circumference * 0.42;
  const outStroke = (1 - inRatio) * circumference * 0.42;
  const gradId = `util-grad-${accentColor.replace('#', '')}-${Math.round(utilization)}`;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        height: '100%',
        borderRadius: 2.5,
        border: `1px solid ${alpha(accentColor, isDark ? 0.22 : 0.16)}`,
        background: getUtilizationBackground(isDark, accentColor),
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <SyncAltOutlinedIcon sx={{ fontSize: 18, color: themeColors.success }} />
        <Typography sx={{ color: themeColors.subtleText, fontSize: '0.72rem', fontWeight: 700, letterSpacing: 0.6 }}>
          {title}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{ position: 'relative', width: 112, height: 112, flexShrink: 0 }}>
          <svg viewBox="0 0 120 120" width="112" height="112" aria-hidden>
            <defs>
              <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor={accentColor} />
                <stop offset="100%" stopColor={themeColors.success} />
              </linearGradient>
            </defs>
            <circle
              cx="60"
              cy="60"
              r={radius}
              fill="none"
              stroke={isDark ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.08)'}
              strokeWidth="9"
            />
            <circle
              cx="60"
              cy="60"
              r={radius}
              fill="none"
              stroke={`url(#${gradId})`}
              strokeWidth="9"
              strokeLinecap="round"
              strokeDasharray={`${utilStroke} ${circumference}`}
              transform="rotate(-90 60 60)"
            />
            <circle
              cx="60"
              cy="60"
              r="30"
              fill="none"
              stroke={themeColors.success}
              strokeWidth="5"
              strokeLinecap="round"
              strokeDasharray={`${inStroke} ${circumference}`}
              transform="rotate(-90 60 60)"
              opacity={0.85}
            />
            <circle
              cx="60"
              cy="60"
              r="30"
              fill="none"
              stroke={themeColors.danger}
              strokeWidth="5"
              strokeLinecap="round"
              strokeDasharray={`${outStroke} ${circumference}`}
              transform={`rotate(${-90 + inRatio * 360 * 0.42} 60 60)`}
              opacity={0.85}
            />
          </svg>
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography sx={{ color: themeColors.text, fontWeight: 800, fontSize: '1.35rem', lineHeight: 1 }}>
              {utilization}%
            </Typography>
          </Box>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ color: themeColors.subtleText, fontSize: '0.76rem', mb: 1.2 }}>
            movement vs on-hand
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.35 }}>
                <Typography sx={{ color: themeColors.success, fontSize: '0.72rem', fontWeight: 600 }}>Stock in</Typography>
                <Typography sx={{ color: themeColors.text, fontSize: '0.72rem', fontWeight: 700 }}>
                  {formatQty(stockIn)}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={movement ? (stockIn / movement) * 100 : 0}
                sx={{
                  height: 5,
                  borderRadius: 999,
                  bgcolor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.08)',
                  '& .MuiLinearProgress-bar': { borderRadius: 999, bgcolor: themeColors.success },
                }}
              />
            </Box>
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.35 }}>
                <Typography sx={{ color: themeColors.danger, fontSize: '0.72rem', fontWeight: 600 }}>Stock out</Typography>
                <Typography sx={{ color: themeColors.text, fontSize: '0.72rem', fontWeight: 700 }}>
                  {formatQty(stockOut)}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={movement ? (stockOut / movement) * 100 : 0}
                sx={{
                  height: 5,
                  borderRadius: 999,
                  bgcolor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.08)',
                  '& .MuiLinearProgress-bar': { borderRadius: 999, bgcolor: themeColors.danger },
                }}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

const PoolInventoryRow = ({
  title,
  icon,
  pool,
  accentColor,
  isDark,
  themeColors,
  rangeLabel,
  loading = false,
}) => {
  const currentQty = Number(pool?.current_inventory_qty || 0);
  const stockIn = Number(pool?.stock_in || 0);
  const stockOut = Number(pool?.stock_out || 0);
  const purchaseQty = Number(pool?.purchase_qty || 0);
  const purchaseAmount = Number(pool?.purchase_amount || 0);
  const maxBar = Math.max(currentQty, stockIn, stockOut, purchaseQty, 1);

  return (
    <Paper elevation={0} sx={{ ...getStockChartCardSx(isDark, themeColors, accentColor), mb: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: accentColor,
              bgcolor: alpha(accentColor, isDark ? 0.16 : 0.12),
              border: `1px solid ${alpha(accentColor, 0.28)}`,
            }}
          >
            {icon}
          </Box>
          <Box>
            <Typography variant="h6" sx={{ color: themeColors.text, fontWeight: 700, lineHeight: 1.2 }}>
              {title}
            </Typography>
            <Typography variant="body2" sx={{ color: themeColors.subtleText, fontSize: '0.8rem' }}>
              {rangeLabel}{loading ? '' : ` · ${pool?.item_count ?? 0} active items`}
            </Typography>
          </Box>
        </Box>
        {loading ? (
          <StatValueSkeleton isDark={isDark} height={16} width={180} />
        ) : (
          <Typography sx={{ color: themeColors.subtleText, fontSize: '0.78rem', textAlign: 'right' }}>
            Value {formatCurrency(pool?.current_inventory_value)}
            {' · '}
            Low stock {Number(pool?.low_stock_count || 0)}
            {' · '}
            Out of stock {Number(pool?.out_of_stock_count || 0)}
          </Typography>
        )}
      </Box>

      <Grid container spacing={2}>
        <Grid item {...getInsightsMetricGridProps()}>
          <MetricTile
            label="CURRENT INVENTORY"
            value={`${formatQty(currentQty)} units`}
            subValue={formatCurrency(pool?.current_inventory_value)}
            color={accentColor}
            icon={<Inventory2OutlinedIcon sx={{ fontSize: 18 }} />}
            progress={(currentQty / maxBar) * 100}
            isDark={isDark}
            themeColors={themeColors}
            loading={loading}
          />
        </Grid>
        <Grid item {...getInsightsMetricGridProps()}>
          <MetricTile
            label="INVENTORY PURCHASE"
            value={formatCurrencyDetailed(purchaseAmount)}
            subValue={`${formatQty(purchaseQty)} units purchased in range`}
            color={themeColors.cyan}
            icon={<ShoppingCartOutlinedIcon sx={{ fontSize: 18 }} />}
            progress={(purchaseQty / maxBar) * 100}
            isDark={isDark}
            themeColors={themeColors}
            loading={loading}
          />
        </Grid>
        <Grid item {...getInsightsMetricGridProps()}>
          <MetricTile
            label="STOCK IN"
            value={`${formatQty(stockIn)} units`}
            subValue="Purchases, transfers in & positive adjustments"
            color={themeColors.success}
            icon={<NorthEastOutlinedIcon sx={{ fontSize: 18 }} />}
            progress={(stockIn / maxBar) * 100}
            isDark={isDark}
            themeColors={themeColors}
            loading={loading}
          />
        </Grid>
        <Grid item {...getInsightsMetricGridProps()}>
          <MetricTile
            label="STOCK OUT"
            value={`${formatQty(stockOut)} units`}
            subValue="Issues, transfers out & negative adjustments"
            color={themeColors.danger}
            icon={<SouthWestOutlinedIcon sx={{ fontSize: 18 }} />}
            progress={(stockOut / maxBar) * 100}
            isDark={isDark}
            themeColors={themeColors}
            loading={loading}
          />
        </Grid>
      </Grid>
    </Paper>
  );
};

const StockPoolInventoryDashboard = ({
  poolInventory,
  isDark,
  themeColors,
  rangeLabel,
  loading = false,
}) => {
  const dailyTrend = Array.isArray(poolInventory?.daily_trend) ? poolInventory.daily_trend : [];

  const trendChartData = useMemo(
    () =>
      dailyTrend.map((row) => ({
        label: row.label || row.date,
        hotelIn: Number(row.hotel_in || 0),
        hotelOut: Number(row.hotel_out || 0),
        restaurantIn: Number(row.restaurant_in || 0),
        restaurantOut: Number(row.restaurant_out || 0),
      })),
    [dailyTrend],
  );

  const hotel = poolInventory?.hotel;
  const restaurant = poolInventory?.restaurant;

  const chartLegendProps = getStockLegendProps(themeColors);
  const chartMarginLegend = getStockChartMargin({ withLegend: true });

  return (
    <Box sx={{ ...getStockDashboardSectionSx(), mb: 0 }}>
      <Box sx={{ mb: 3 }}>
        <PoolInventoryRow
          title="Restaurant Inventory"
          icon={<RestaurantOutlinedIcon />}
          pool={restaurant}
          accentColor={themeColors.info}
          isDark={isDark}
          themeColors={themeColors}
          rangeLabel={rangeLabel}
          loading={loading}
        />
      </Box>

      <Box sx={{ mb: 3 }}>
        <PoolInventoryRow
          title="Hotel Inventory"
          icon={<HotelOutlinedIcon />}
          pool={hotel}
          accentColor={themeColors.warning}
          isDark={isDark}
          themeColors={themeColors}
          rangeLabel={rangeLabel}
          loading={loading}
        />
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <UtilizationFlowVisual
            title="RESTAURANT STOCK UTILIZATION"
            utilization={computePoolUtilization(restaurant)}
            stockIn={Number(restaurant?.stock_in || 0)}
            stockOut={Number(restaurant?.stock_out || 0)}
            accentColor={themeColors.info}
            themeColors={themeColors}
            isDark={isDark}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <UtilizationFlowVisual
            title="HOTEL STOCK UTILIZATION"
            utilization={computePoolUtilization(hotel)}
            stockIn={Number(hotel?.stock_in || 0)}
            stockOut={Number(hotel?.stock_out || 0)}
            accentColor={themeColors.warning}
            themeColors={themeColors}
            isDark={isDark}
            loading={loading}
          />
        </Grid>
      </Grid>

      <Paper elevation={0} sx={{ ...getStockChartCardSx(isDark, themeColors, themeColors.cyan), mb: 0 }}>
        <Typography variant="h6" sx={{ color: themeColors.text, fontWeight: 700, mb: 0.5 }}>
          Daily Stock Movement
        </Typography>
        <Typography variant="body2" sx={{ color: themeColors.subtleText, mb: 2 }}>
          Hotel vs restaurant stock in and stock out ({rangeLabel})
        </Typography>
        {loading ? (
          <ChartAreaSkeleton isDark={isDark} height={300} />
        ) : !trendChartData.length ? (
          <Typography sx={{ color: themeColors.subtleText, fontSize: '0.86rem' }}>
            No stock movement data for the selected range.
          </Typography>
        ) : (
          <Box sx={getStockChartPlotSx(isDark, themeColors, 300)}>
            <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={300}>
              <BarChart data={trendChartData} margin={chartMarginLegend}>
                <CartesianGrid strokeDasharray="3 3" stroke={themeColors.surfaceBorder} opacity={isDark ? 0.2 : 0.3} />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: themeColors.text }} stroke={themeColors.surfaceBorder} />
                <YAxis tick={{ fontSize: 11, fill: themeColors.text }} stroke={themeColors.surfaceBorder} />
                <Tooltip
                  formatter={(value) => [`${formatQty(value)} units`, '']}
                  contentStyle={{
                    background: isDark ? '#1e293b' : '#fff',
                    border: `1px solid ${themeColors.surfaceBorder}`,
                    borderRadius: 8,
                  }}
                />
                <Legend {...chartLegendProps} />
                <Bar dataKey="hotelIn" name="Hotel Stock In" fill={themeColors.warning} radius={[4, 4, 0, 0]} />
                <Bar dataKey="hotelOut" name="Hotel Stock Out" fill={alpha(themeColors.warning, 0.45)} radius={[4, 4, 0, 0]} />
                <Bar dataKey="restaurantIn" name="Restaurant Stock In" fill={themeColors.info} radius={[4, 4, 0, 0]} />
                <Bar dataKey="restaurantOut" name="Restaurant Stock Out" fill={alpha(themeColors.info, 0.45)} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default StockPoolInventoryDashboard;
